// SPDX-License-Identifier: AGPL-3.0-or-later

import {observeResize} from '@app/features/platform/utils/SharedResizeObserver';
import {type RefObject, useEffect, useState} from 'react';

interface UseTextOverflowOptions {
	content: string | null | undefined;
	checkVertical?: boolean;
	measureTextRange?: boolean;
}

const OVERFLOW_EPSILON_PX = 1;

interface NaturalInlineMeasurement {
	clone: HTMLElement;
	availableWidth: number;
	updateOverflowing: (next: boolean) => void;
}

type OverflowCheck = () => NaturalInlineMeasurement | undefined;

interface OverflowBatch {
	checks: Set<OverflowCheck>;
	frameId: number;
}

const overflowBatches = new WeakMap<Window, OverflowBatch>();

function queueOverflowCheck(ownerWindow: Window, check: OverflowCheck): void {
	const pending = overflowBatches.get(ownerWindow);
	if (pending) {
		pending.checks.add(check);
		return;
	}
	const batch: OverflowBatch = {checks: new Set([check]), frameId: 0};
	overflowBatches.set(ownerWindow, batch);
	batch.frameId = ownerWindow.requestAnimationFrame(() => {
		if (overflowBatches.get(ownerWindow) !== batch) return;
		overflowBatches.delete(ownerWindow);
		const measurements: Array<NaturalInlineMeasurement> = [];
		try {
			for (const check of batch.checks) {
				try {
					const measurement = check();
					if (measurement) measurements.push(measurement);
				} catch (error) {
					console.error('Text overflow check threw:', error);
				}
			}
			if (measurements.length === 0) return;
			// Finish source reads before inserting clones, then read every clone before removing any.
			const fragment = ownerWindow.document.createDocumentFragment();
			for (const {clone} of measurements) fragment.appendChild(clone);
			ownerWindow.document.body.appendChild(fragment);
			for (const {clone, availableWidth, updateOverflowing} of measurements) {
				try {
					const naturalInlineSize = Math.max(clone.scrollWidth, clone.getBoundingClientRect().width);
					updateOverflowing(isMeaningfullyGreater(naturalInlineSize, availableWidth));
				} catch (error) {
					console.error('Text overflow measurement threw:', error);
				}
			}
		} finally {
			batch.checks.clear();
			for (const {clone} of measurements) clone.remove();
		}
	});
}

function cancelOverflowCheck(ownerWindow: Window, check: OverflowCheck): void {
	const batch = overflowBatches.get(ownerWindow);
	if (!batch) return;
	batch.checks.delete(check);
	if (batch.checks.size === 0) {
		overflowBatches.delete(ownerWindow);
		ownerWindow.cancelAnimationFrame(batch.frameId);
	}
}

function isMeaningfullyGreater(measuredSize: number, availableSize: number): boolean {
	return measuredSize - availableSize > OVERFLOW_EPSILON_PX;
}

function getAvailableInlineSize(element: HTMLElement, clientWidth: number): number {
	if (clientWidth > 0) {
		return clientWidth;
	}
	return element.getBoundingClientRect().width;
}

function measureRangeInlineSize(element: HTMLElement): number {
	const range = element.ownerDocument.createRange();
	try {
		range.selectNodeContents(element);
		return range.getBoundingClientRect().width;
	} finally {
		range.detach?.();
	}
}

function createNaturalInlineSizeClone(element: HTMLElement): HTMLElement | null {
	const ownerDocument = element.ownerDocument;
	if (!ownerDocument.body) return null;
	const computedStyle = ownerDocument.defaultView?.getComputedStyle(element);
	if (
		!computedStyle ||
		(computedStyle.textOverflow === 'clip' &&
			computedStyle.whiteSpace !== 'nowrap' &&
			computedStyle.whiteSpace !== 'pre')
	) {
		return null;
	}
	const clone = element.cloneNode(true) as HTMLElement;
	clone.style.font = computedStyle.font;
	clone.style.fontKerning = computedStyle.fontKerning;
	clone.style.letterSpacing = computedStyle.letterSpacing;
	clone.style.lineHeight = computedStyle.lineHeight;
	clone.style.textTransform = computedStyle.textTransform;
	clone.style.wordSpacing = computedStyle.wordSpacing;
	Object.assign(clone.style, {
		position: 'absolute',
		left: '-100000px',
		top: '-100000px',
		display: 'inline-block',
		width: 'auto',
		minWidth: '0',
		maxWidth: 'none',
		height: 'auto',
		maxHeight: 'none',
		overflow: 'visible',
		overflowX: 'visible',
		overflowY: 'visible',
		whiteSpace: 'nowrap',
		textOverflow: 'clip',
		visibility: 'hidden',
		pointerEvents: 'none',
		zIndex: '-1',
	} satisfies Partial<CSSStyleDeclaration>);
	return clone;
}

export function useTextOverflow(
	elementRef: RefObject<HTMLElement | null>,
	{content, checkVertical = false, measureTextRange = false}: UseTextOverflowOptions,
): boolean {
	const [isOverflowing, setIsOverflowing] = useState(false);
	useEffect(() => {
		const element = elementRef.current;
		const ownerWindow = element?.ownerDocument.defaultView;
		if (!element || !ownerWindow || !content) {
			setIsOverflowing(false);
			return;
		}
		let disposed = false;
		const updateOverflowing = (next: boolean) => {
			if (disposed) return;
			setIsOverflowing((previous) => (previous === next ? previous : next));
		};
		const checkOverflow: OverflowCheck = () => {
			if (disposed) return;
			const clientWidth = element.clientWidth;
			if (
				isMeaningfullyGreater(element.scrollWidth, clientWidth) ||
				(checkVertical && isMeaningfullyGreater(element.scrollHeight, element.clientHeight))
			) {
				updateOverflowing(true);
				return;
			}
			if (measureTextRange) {
				const availableWidth = getAvailableInlineSize(element, clientWidth);
				if (isMeaningfullyGreater(measureRangeInlineSize(element), availableWidth)) {
					updateOverflowing(true);
					return;
				}
				const clone = createNaturalInlineSizeClone(element);
				if (clone) return {clone, availableWidth, updateOverflowing};
			}
			updateOverflowing(false);
			return undefined;
		};
		const scheduleOverflowCheck = () => {
			if (disposed) {
				return;
			}
			queueOverflowCheck(ownerWindow, checkOverflow);
		};
		scheduleOverflowCheck();
		const unobserveResize =
			typeof ResizeObserver !== 'undefined' ? observeResize(element, scheduleOverflowCheck) : undefined;
		let mutationObserver: MutationObserver | null = null;
		if (typeof ownerWindow.MutationObserver !== 'undefined') {
			mutationObserver = new ownerWindow.MutationObserver(scheduleOverflowCheck);
			mutationObserver.observe(element, {
				attributes: true,
				attributeFilter: ['alt', 'class', 'src', 'style'],
				childList: true,
				characterData: true,
				subtree: true,
			});
		}
		element.addEventListener('load', scheduleOverflowCheck, true);
		const fontSet = element.ownerDocument.fonts;
		fontSet?.ready?.then(scheduleOverflowCheck);
		fontSet?.addEventListener?.('loadingdone', scheduleOverflowCheck);
		return () => {
			disposed = true;
			cancelOverflowCheck(ownerWindow, checkOverflow);
			unobserveResize?.();
			mutationObserver?.disconnect();
			element.removeEventListener('load', scheduleOverflowCheck, true);
			fontSet?.removeEventListener?.('loadingdone', scheduleOverflowCheck);
		};
	}, [checkVertical, content, elementRef, measureTextRange]);
	return isOverflowing;
}
